import cn from 'classnames';
import { toZonedTime } from 'date-fns-tz';
import { Fragment, useMemo } from 'react';
import { HOUR_HEIGHT } from '../constants';
import { useGetZonedSlotStart } from '../hooks';
import { SlotButton } from '../slot-button';
import type { CalendarMode, RenderTimeSlot } from '../types';
import { useGetHoursOfDay } from './hooks';
import styles from './hours.module.css';
import { TimeColumn } from './time-column';

type Properties = {
  days: Date[];
  mode: CalendarMode;
  orderedTimezones: string[];
  renderTimeSlot?: RenderTimeSlot;
  timeSlotDurationInMinutes?: number;
  timezone: string;
};

const Hours = ({
  days,
  mode,
  orderedTimezones,
  renderTimeSlot,
  timeSlotDurationInMinutes = 30,
  timezone,
}: Properties) => {
  const hours = useGetHoursOfDay();
  const getZonedSlotStart = useGetZonedSlotStart();

  const slotMinutes = useMemo(
    () =>
      Array.from(
        { length: Math.floor(60 / Math.max(1, timeSlotDurationInMinutes)) },
        (_, index) => index * timeSlotDurationInMinutes,
      ),
    [timeSlotDurationInMinutes],
  );

  const hourUtcPairs = useMemo(() => {
    const baseDay = days[0] ?? new Date();

    return hours.map((hour) => [hour, getZonedSlotStart(baseDay, timezone, hour)] as const);
  }, [days, getZonedSlotStart, hours, timezone]);

  return hourUtcPairs.map(([hour, utcDate]) => (
    <div className={styles.hours} key={hour}>
      {orderedTimezones.map((tz) => (
        <TimeColumn date={toZonedTime(utcDate, tz)} key={tz} />
      ))}

      <div className={cn(styles['time-slot'], { [styles['week-grid'] ?? '']: mode === 'week' })}>
        {days.map((d) => (
          <div className={styles['event-day']} key={d.toISOString()}>
            {renderTimeSlot
              ? slotMinutes.map((minute) => {
                  const slotStart = getZonedSlotStart(d, timezone, hour, minute);
                  return (
                    <Fragment key={minute}>
                      {renderTimeSlot({
                        children: (
                          <SlotButton
                            className={styles.slot}
                            style={{ height: `${(HOUR_HEIGHT * timeSlotDurationInMinutes) / 60}px` }}
                          />
                        ),
                        slotStart,
                      })}
                    </Fragment>
                  );
                })
              : null}
          </div>
        ))}
      </div>
    </div>
  ));
};

export default Hours;
